import React, { FC } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { html } from 'lit/static-html.js';
import {
  Button,
  DataTable,
  Icon,
  PageSection,
  Spacer,
  Stack,
  TextTitle,
  Dropdown,
  Menu,
  MenuItem,
  logos,
} from '@pypestream/design-system';
import { formatTimeWithTimezone } from '@pypestream/utils';

import {
  useConnectAspModal,
  useConnectAiProviderModal,
  useDeleteProviderModal,
} from '../components';
import { WithLoadingState } from '../router';
import { StudioPageProps } from '../hooks';

type ProjectConfigurationsProps = StudioPageProps;

interface HardcodedAspDataType {
  id: string;
  name: string;
  env: string;
  platform: string;
  solutions: string[];
  type: string;
}

interface HardcodedAiProviderDataType {
  id: string;
  name: string;
  provider: 'gemini' | 'open-ai' | 'anthropic';
  env: string;
  model: string;
  solutions: string[];
  type: string;
}

const aspData = [
  {
    id: 'asp-data-1',
    name: 'ADTContactCenter Live',
    env: 'Live',
    platform: 'cc',
    solutions: [
      'CustomerAcquisitionSolution',
      'WifiFix',
      'New Products',
      'New Products 1',
      'New Products 2',
      'New Products 3',
    ],
    type: 'live',
  },
  {
    id: 'asp-data-2',
    name: 'ADTContactCenter Sandbox',
    env: 'Sandbox',
    platform: 'cc',
    solutions: [
      'CustomerAcquisitionSolution',
      'WifiFix',
      'New Products',
      'New Products 1',
      'New Products 2',
      'New Products 3',
    ],
    type: 'sandbox',
  },
  {
    id: 'asp-data-3',
    name: 'ADTSalesforceConfig Sandbox',
    env: 'Sandbox',
    platform: 'salesforce',
    solutions: ['WifiFix', 'New Products'],
    type: 'sandbox',
  },
  {
    id: 'asp-data-4',
    name: 'ADT Quiq Config Live',
    env: 'Live',
    platform: 'q',
    solutions: ['New Products'],
    type: 'live',
  },
  {
    id: 'asp-data-5',
    name: 'ADT Quiq Config Sandbox',
    env: 'Sandbox',
    platform: 'q',
    solutions: ['WifiFix'],
    type: 'sandbox',
  },
];

const aiProviderData = [
  {
    id: 'ai-data-1',
    name: 'Gemini Main',
    provider: 'gemini',
    env: 'Live',
    model: 'cc',
    solutions: [
      'CustomerAcquisitionSolution',
      'WifiFix',
      'New Products',
      'New Products 1',
      'New Products 2',
      'New Products 3',
    ],
    type: 'live',
  },
  {
    id: 'ai-data-2',
    name: 'Gemini 2',
    provider: 'gemini',
    env: 'Live',
    model: 'q',
    solutions: ['New Products'],
    type: 'live',
  },
  {
    id: 'ai-data-3',
    name: 'Gemini 3',
    provider: 'gemini',
    env: 'Sandbox',
    model: 'q',
    solutions: ['WifiFix'],
    type: 'sandbox',
  },
];

const ProjectConfigurationsPage: FC<ProjectConfigurationsProps> = () => {
  const { openModal: openAspModal, connectAspModal } = useConnectAspModal();
  const { openModal: openAiProviderModal, connectAiProviderModal } =
    useConnectAiProviderModal();
  const { openModal: openDeleteProviderModal, deleteProviderModal } =
    useDeleteProviderModal();

  const aspColumns: ColumnDef<HardcodedAspDataType>[] = [
    {
      accessorKey: 'name',
      accessorFn: (row) => row.name,
      header: 'Name',
      cell: ({ row }) =>
        html`<div style="width: 260px;">
          <ps-text-subtitle size="small">
            ${row?.original?.name}
          </ps-text-subtitle>
        </div>`,
    },
    {
      accessorKey: 'platform',
      accessorFn: (row) => row.name,
      header: 'Platform',
      cell: () =>
        html`<ps-stack justifyContent="center"
          ><ps-image
            width="24"
            height="24"
            src=${logos.contactCenter}
          ></ps-image>
        </ps-stack>`,
      meta: {
        align: 'center',
      },
    },
    {
      accessorKey: 'solutions',
      accessorFn: (row) => row.name,
      header: 'Solutions Using it',
      cell: ({ row }) => {
        const solutions = row?.original?.solutions;
        const type = row?.original?.type;

        return html`<ps-stack nowrap>
          ${solutions
            .slice(0, 3)
            .map(
              (solution) =>
                html`<ps-tag
                  size="small"
                  minimal
                  variant=${type === 'live' ? 'success' : 'default'}
                  >${solution}</ps-tag
                >`
            )}
          ${solutions.length > 3
            ? html`<ps-tag
                size="small"
                minimal
                variant=${type === 'live' ? 'success' : 'default'}
                >+${solutions.length - 3}</ps-tag
              >`
            : null}
        </ps-stack>`;
      },
    },
    {
      accessorKey: 'userName',
      header: 'Last updated',
      cell: () => {
        // if (!row || !row?.original) {
        //   return null;
        // }

        // const { first_name, last_name, profilePhoto, updated } = row.original;
        // const userName = `${first_name} ${last_name}`;
        const userName = `Firstname Lastname`;

        return html`<ps-stack
          alignItems="center"
          justifyContent="end"
          gutter="xsmall"
          nowrap
        >
          <ps-text-body size="small" truncate>
            ${formatTimeWithTimezone({
              fromDate: '2025-05-09 20:18:46+0000',
              // fromDate: updated,
              // locale: dateFnsLocales[defaultLanguage || fallbackLanguage],
              // userTimeZone,
            })}
            ${' '}by
          </ps-text-body>
          <ps-tooltip content=${userName}>
            <ps-avatar size="small" label=${userName}></ps-avatar>
          </ps-tooltip>
        </ps-stack>`;
      },
      meta: {
        align: 'end',
      },
    },
    {
      id: 'actions',
      header: () =>
        html`<span style="opacity: 0; font-size: 4px;">Actions</span>`,
      cell: ({ row }) => html`
        <ps-stack justifyContent="end">
          <ps-icon-button
            name="kebab"
            size="small"
            id=${`ai-provider-actions-dropdown-${row?.original?.id}`}
          ></ps-icon-button>
        </ps-stack>
        <ps-dropdown
          trigger=${`ai-provider-actions-dropdown-${row?.original?.id}`}
          placement="bottom-end"
        >
          <ps-menu>
            <ps-menu-item @click=${() => openAspModal('edit')}
              ><ps-icon name="edit" size="small" slot="prefix"></ps-icon>
              Edit</ps-menu-item
            >
            <ps-menu-item
              @click=${() => openDeleteProviderModal(row?.original?.name)}
              ><ps-icon name="delete" size="small" slot="prefix"></ps-icon>
              Delete</ps-menu-item
            >
          </ps-menu>
        </ps-dropdown>
      `,
      meta: {
        align: 'end',
      },
    },
  ];

  const aiProviderColumns: ColumnDef<HardcodedAiProviderDataType>[] = [
    {
      accessorKey: 'name',
      accessorFn: (row) => row.name,
      header: 'Name',
      cell: ({ row }) =>
        html`<div style="width: 260px;">
          <ps-text-subtitle size="small">
            ${row?.original?.name}
          </ps-text-subtitle>
        </div>`,
    },
    {
      accessorKey: 'model',
      accessorFn: (row) => row.name,
      header: 'Provider',
      cell: () =>
        html`<ps-stack justifyContent="center"
          ><ps-icon name="gemini" size="small"></ps-icon
        ></ps-stack>`,
      meta: {
        align: 'center',
      },
    },
    {
      accessorKey: 'solutions',
      accessorFn: (row) => row.name,
      header: 'Solutions Using it',
      cell: ({ row }) => {
        const solutions = row?.original?.solutions;
        const type = row?.original?.type;

        return html`<ps-stack nowrap>
          ${solutions
            .slice(0, 3)
            .map(
              (solution) =>
                html`<ps-tag
                  size="small"
                  minimal
                  variant=${type === 'live' ? 'success' : 'default'}
                  >${solution}</ps-tag
                >`
            )}
          ${solutions.length > 3
            ? html`<ps-tag
                size="small"
                minimal
                variant=${type === 'live' ? 'success' : 'default'}
                >+${solutions.length - 3}</ps-tag
              >`
            : null}
        </ps-stack>`;
      },
    },
    {
      accessorKey: 'userName',
      header: 'Last updated',
      cell: () => {
        // if (!row || !row?.original) {
        //   return null;
        // }

        // const { first_name, last_name, profilePhoto, updated } = row.original;
        // const userName = `${first_name} ${last_name}`;
        const userName = `Firstname Lastname`;

        return html`<ps-stack
          alignItems="center"
          justifyContent="end"
          gutter="xsmall"
          nowrap
        >
          <ps-text-body size="small" truncate>
            ${formatTimeWithTimezone({
              fromDate: '2025-05-09 20:18:46+0000',
              // fromDate: updated,
              // locale: dateFnsLocales[defaultLanguage || fallbackLanguage],
              // userTimeZone,
            })}
            ${' '}by
          </ps-text-body>
          <ps-tooltip content=${userName}>
            <ps-avatar size="small" label=${userName}></ps-avatar>
          </ps-tooltip>
        </ps-stack>`;
      },
      meta: {
        align: 'end',
      },
    },
    {
      id: 'actions',
      header: () =>
        html`<span style="opacity: 0; font-size: 4px;">Actions</span>`,
      cell: ({ row }) => html`
        <ps-stack justifyContent="end">
          <ps-icon-button
            name="kebab"
            size="small"
            id=${`ai-provider-actions-dropdown-${row?.original?.id}`}
          ></ps-icon-button>
        </ps-stack>
        <ps-dropdown
          trigger=${`ai-provider-actions-dropdown-${row?.original?.id}`}
          placement="bottom-end"
        >
          <ps-menu>
            <ps-menu-item
              @click=${() =>
                openAiProviderModal(row?.original?.provider, 'edit')}
              ><ps-icon name="edit" size="small" slot="prefix"></ps-icon>
              Edit</ps-menu-item
            >
            <ps-menu-item
              @click=${() => openDeleteProviderModal(row?.original?.name)}
              ><ps-icon name="delete" size="small" slot="prefix"></ps-icon>
              Delete</ps-menu-item
            >
          </ps-menu>
        </ps-dropdown>
      `,
      meta: {
        align: 'end',
      },
    },
  ];

  return (
    <React.Fragment>
      <TextTitle size="small">Project Configurations</TextTitle>
      <Spacer size="large" />
      <PageSection size="small">
        <Stack alignItems="center" justifyContent="space-between">
          <TextTitle size="xsmall">Agent Platform</TextTitle>

          <Button
            test-id="ps-homepage-create-solution-btn"
            size="large"
            onClick={() => openAspModal('connect')}
          >
            <Icon name="add" slot="prefix" />
            Add
          </Button>
        </Stack>

        <Spacer size="xlarge" />

        <DataTable
          filterColumns={['name']}
          size="small"
          columns={aspColumns}
          data={aspData}
          actionable={false}
        />
      </PageSection>
      <PageSection test-id="ps-homepage-page-section" size="small">
        <Stack alignItems="center" justifyContent="space-between">
          <TextTitle test-id="ps-homepage-title" size="xsmall">
            AI Provider
          </TextTitle>

          <Button
            test-id="ps-homepage-create-solution-btn"
            size="large"
            id="ai-provider-connection"
          >
            <Icon name="add" slot="prefix" />
            Add
          </Button>
          <Dropdown trigger="ai-provider-connection" placement="bottom-start">
            <Menu>
              <MenuItem
                onClick={() => {
                  openAiProviderModal('gemini');
                }}
              >
                <Icon slot="prefix" name="gemini" size="small" />
                Gemini
              </MenuItem>
              <MenuItem onClick={() => openAiProviderModal('open-ai')}>
                <Icon slot="prefix" name="gpt" size="small" /> Open AI
              </MenuItem>
              <MenuItem onClick={() => openAiProviderModal('anthropic')}>
                <Icon slot="prefix" name="claude" size="small" /> Anthropic
              </MenuItem>
            </Menu>
          </Dropdown>
        </Stack>

        <Spacer size="xlarge" />

        <DataTable
          filterColumns={['name']}
          size="small"
          columns={aiProviderColumns}
          data={aiProviderData}
          actionable={false}
        />
      </PageSection>

      {connectAspModal}
      {connectAiProviderModal}
      {deleteProviderModal}
    </React.Fragment>
  );
};

export const ProjectConfigurations = WithLoadingState(
  ProjectConfigurationsPage
);
